/**
 * JWT decoder — pure, deterministic, DECODE ONLY.
 *
 * This splits a JWT into its three parts and base64url-decodes the header and
 * payload as JSON. It does NOT verify the signature — never treat a decoded
 * token as authenticated. Decoding is deterministic, so it is safe to render
 * server-side.
 */

export interface DecodedJwt {
  ok: true;
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  signature: string;
}

export type DecodeResult = DecodedJwt | { ok: false; error: string };

function base64UrlDecode(input: string): string {
  let b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const remainder = b64.length % 4;
  if (remainder === 2) b64 += "==";
  else if (remainder === 3) b64 += "=";
  else if (remainder === 1) throw new Error("Invalid base64url length");

  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function decodeJwt(token: string): DecodeResult {
  const t = token.trim();
  if (!t) return { ok: false, error: "Enter a JWT." };

  const parts = t.split(".");
  if (parts.length !== 3) {
    return {
      ok: false,
      error: `A JWT has 3 dot-separated parts; found ${parts.length}.`,
    };
  }

  let header: unknown;
  try {
    header = JSON.parse(base64UrlDecode(parts[0]));
  } catch {
    return {
      ok: false,
      error: "Could not decode the header (invalid base64url or JSON).",
    };
  }

  let payload: unknown;
  try {
    payload = JSON.parse(base64UrlDecode(parts[1]));
  } catch {
    return {
      ok: false,
      error: "Could not decode the payload (invalid base64url or JSON).",
    };
  }

  if (!isPlainObject(header)) {
    return { ok: false, error: "Header is not a JSON object." };
  }
  if (!isPlainObject(payload)) {
    return { ok: false, error: "Payload is not a JSON object." };
  }

  return { ok: true, header, payload, signature: parts[2] };
}

const TIME_CLAIMS = ["iat", "nbf", "exp"] as const;

/**
 * Surface known time-based claims as ISO 8601 (UTC) strings. Uses
 * `new Date(seconds * 1000)` — deterministic from the token, never the current
 * clock — so it is safe to call during render.
 */
export function claimDates(
  payload: Record<string, unknown>,
): Array<{ claim: string; iso: string }> {
  const out: Array<{ claim: string; iso: string }> = [];
  for (const claim of TIME_CLAIMS) {
    if (!Object.hasOwn(payload, claim)) continue;
    const v = payload[claim];
    if (typeof v === "number" && Number.isFinite(v)) {
      out.push({ claim, iso: new Date(v * 1000).toISOString() });
    }
  }
  return out;
}
